import { useEffect, useRef, useState } from 'react';
import { useAuth } from '@/auth/AuthContext';
import { getEndpoints } from '@/api/endpoints';
import { Field, FormCard, TextInput } from './_shared';
import type { PlaygroundProps } from './index';

interface LogEntry {
  ts: number;
  direction: 'in' | 'out' | 'system';
  payload: string;
}

export function WebSocketPlayground({ mode }: PlaygroundProps) {
  const { env, getValidAccessToken } = useAuth();
  const [projectId, setProjectId] = useState('proj_xxx');
  const [log, setLog] = useState<LogEntry[]>([]);
  const [connected, setConnected] = useState(false);
  const wsRef = useRef<WebSocket | null>(null);

  useEffect(() => () => wsRef.current?.close(), []);

  const append = (direction: LogEntry['direction'], payload: string) =>
    setLog((prev) => [...prev, { ts: Date.now(), direction, payload }]);

  const connect = async () => {
    if (mode !== 'live') {
      append('system', 'WebSocket requires Live mode (auth token).');
      return;
    }
    const token = await getValidAccessToken();
    if (!token) {
      append('system', 'No access token available — sign in first.');
      return;
    }
    // Step 1: get a transfer token (single-use, 120s expiry).
    append('system', 'Requesting transfer token…');
    const apiBase = getEndpoints(env).apiBase;
    let transferToken: string;
    try {
      const resp = await fetch(`${apiBase}/api/tokens/transfer`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = await resp.json();
      transferToken = data.transfer_token;
    } catch (e) {
      append('system', `Failed to get transfer token: ${e instanceof Error ? e.message : e}`);
      return;
    }

    // Step 2: connect to the WS with the transfer token.
    const wsUrl = `${getEndpoints(env).websocket}?token=${encodeURIComponent(transferToken)}`;
    append('system', `Connecting to ${wsUrl}`);
    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;
    ws.onopen = () => {
      setConnected(true);
      append('system', 'Connected.');
      const sub = JSON.stringify({ action: 'subscribe', project_id: projectId });
      ws.send(sub);
      append('out', sub);
    };
    ws.onmessage = (ev) => append('in', typeof ev.data === 'string' ? ev.data : '[binary]');
    ws.onerror = () => append('system', 'Socket error.');
    ws.onclose = (ev) => {
      setConnected(false);
      append('system', `Closed (code ${ev.code}).`);
    };
  };

  const disconnect = () => {
    wsRef.current?.close();
    wsRef.current = null;
  };

  return (
    <div className="lp-grid lp-grid-cols-1 xl:lp-grid-cols-[minmax(0,1fr)_440px] lp-gap-6">
      <div className="lp-space-y-5">
        <div>
          <h1 className="lp-text-2xl lp-font-bold lp-text-ink-900 lp-tracking-tight">
            WebSocket notifications
          </h1>
          <p className="lp-text-sm lp-text-ink-500 lp-mt-1">
            Subscribe to <code>ProjectStatusChanged</code>, <code>DocumentStatusChanged</code>,{' '}
            <code>StoryboardStatusChanged</code> and <code>MediaStatusChanged</code> events for a project.
          </p>
        </div>
        <FormCard>
          <Field label="Project ID">
            <TextInput value={projectId} onChange={(e) => setProjectId(e.target.value)} />
          </Field>
          <div className="lp-flex lp-gap-2 lp-pt-2">
            {!connected ? (
              <button
                onClick={connect}
                className="lp-flex-1 lp-py-2.5 lp-rounded-xl lp-bg-brand-600 hover:lp-bg-brand-700 lp-text-white lp-text-sm lp-font-semibold"
              >
                Connect & subscribe
              </button>
            ) : (
              <button
                onClick={disconnect}
                className="lp-flex-1 lp-py-2.5 lp-rounded-xl lp-bg-rose-600 hover:lp-bg-rose-700 lp-text-white lp-text-sm lp-font-semibold"
              >
                Disconnect
              </button>
            )}
            <button
              onClick={() => setLog([])}
              className="lp-py-2.5 lp-px-4 lp-rounded-xl lp-bg-ink-100 hover:lp-bg-ink-200 lp-text-ink-700 lp-text-sm lp-font-semibold"
            >
              Clear
            </button>
          </div>
        </FormCard>
      </div>
      <div className="lp-rounded-2xl lp-overflow-hidden lp-bg-ink-900 lp-shadow-card lp-ring-1 lp-ring-ink-900/10">
        <div className="lp-px-4 lp-py-2.5 lp-bg-ink-950/40 lp-border-b lp-border-white/5 lp-flex lp-items-center lp-justify-between">
          <span className="lp-text-xs lp-font-mono lp-text-ink-300">events stream</span>
          <span
            className={`lp-text-[10px] lp-font-bold lp-px-1.5 lp-py-0.5 lp-rounded lp-tracking-wider ${
              connected ? 'lp-bg-emerald-500/15 lp-text-emerald-300' : 'lp-bg-white/5 lp-text-ink-400'
            }`}
          >
            {connected ? 'OPEN' : 'CLOSED'}
          </span>
        </div>
        <pre className="lp-code-pane lp-text-[12px] lp-leading-relaxed lp-text-ink-100 lp-font-mono lp-p-4 lp-overflow-auto lp-max-h-[520px]">
          {log.length === 0
            ? <span className="lp-text-ink-500 lp-italic">No events yet.</span>
            : log.map((e, i) => (
                <div key={i} className={dirClass(e.direction)}>
                  <span className="lp-text-ink-500">{new Date(e.ts).toLocaleTimeString()} </span>
                  <span className="lp-font-bold">{prefix(e.direction)} </span>
                  {e.payload}
                </div>
              ))}
        </pre>
      </div>
    </div>
  );
}

function dirClass(d: LogEntry['direction']): string {
  switch (d) {
    case 'in':
      return 'lp-text-emerald-300';
    case 'out':
      return 'lp-text-blue-300';
    default:
      return 'lp-text-ink-400';
  }
}
function prefix(d: LogEntry['direction']): string {
  switch (d) {
    case 'in':
      return '←';
    case 'out':
      return '→';
    default:
      return '·';
  }
}
